import { create } from 'zustand';
import { directDraftsApi } from '../services/api';
import { Draft, DraftTypeInfo } from '../types';

/**
 * Cache for the drafts library + the drafting-type registry, following the
 * jobsStore/mattersStore cache-then-revalidate pattern: pages render instantly
 * from cache while a refresh runs in the background.
 *
 * The registry (GET /drafts/types) is static per deploy — fetched once per
 * session and shared by NewDraftPage and DraftsPanel.
 */
interface DraftsState {
  drafts: Draft[];
  lastFetched: number | null;
  isFetching: boolean;
  types: DraftTypeInfo[];
  typesFetched: boolean;
  fetchDrafts: () => Promise<void>;
  fetchTypes: () => Promise<void>;
  removeDraft: (id: string) => void;
  addDraft: (draft: Draft) => void;
}

export const useDraftsStore = create<DraftsState>((set, get) => ({
  drafts: [],
  lastFetched: null,
  isFetching: false,
  types: [],
  typesFetched: false,
  fetchDrafts: async () => {
    if (get().isFetching) return;
    set({ isFetching: true });
    try {
      const { data } = await directDraftsApi.list();
      set({ drafts: data, lastFetched: Date.now(), isFetching: false });
    } catch {
      set({ isFetching: false });
    }
  },
  fetchTypes: async () => {
    if (get().typesFetched && get().types.length > 0) return;
    try {
      const { data } = await directDraftsApi.types();
      set({ types: data, typesFetched: true });
    } catch {
      /* non-fatal — pages fall back to legacy labels */
    }
  },
  removeDraft: (id) => set({ drafts: get().drafts.filter((d) => d.id !== id) }),
  addDraft: (draft) => set({ drafts: [draft, ...get().drafts] }),
}));
